import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ArrowLeft, Heart, CheckCircle2, Trash2, Loader2, Send, Pin, Megaphone } from "lucide-react";
import { toast } from "sonner";
import ForumAttach from "@/components/ForumAttach";
import Attachments from "@/components/Attachments";

export default function ForumTopic() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [topic, setTopic] = useState(null);
  const [reply, setReply] = useState("");
  const [replyAtt, setReplyAtt] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/forum/topics/${id}`);
      setTopic(data);
    } catch {
      toast.error("Nie znaleziono wątku");
      navigate("/forum");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const canManage = (authorId) => topic && (user?.id === authorId || user?.role === "admin");

  const submitReply = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/forum/topics/${id}/replies`, { body: reply, attachments: replyAtt });
      setReply("");
      setReplyAtt([]);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const likeTopic = async () => {
    const { data } = await api.post(`/forum/topics/${id}/like`);
    setTopic((t) => ({ ...t, like_count: data.like_count, liked: data.liked }));
  };
  const likeReply = async (rid) => {
    const { data } = await api.post(`/forum/replies/${rid}/like`);
    setTopic((t) => ({ ...t, replies: t.replies.map((r) => (r.id === rid ? { ...r, like_count: data.like_count, liked: data.liked } : r)) }));
  };
  const toggleSolved = async () => {
    const { data } = await api.post(`/forum/topics/${id}/solve`);
    setTopic((t) => ({ ...t, solved: data.solved }));
    toast.success(data.solved ? "Oznaczono jako rozwiązany" : "Zdjęto oznaczenie");
  };
  const deleteTopic = async () => {
    await api.delete(`/forum/topics/${id}`);
    toast.success("Wątek usunięty");
    navigate("/forum");
  };
  const deleteReply = async (rid) => {
    await api.delete(`/forum/replies/${rid}`);
    load();
  };

  if (!topic) return <div className="flex min-h-screen items-center justify-center pt-[68px]"><Loader2 className="h-8 w-8 animate-spin text-ztk-blue" /></div>;

  const initials = (name) => (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("");

  return (
    <div className="pt-[68px]">
      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
        <Button asChild variant="ghost" size="sm" className="mb-6 gap-1.5 text-muted-foreground">
          <Link to="/forum"><ArrowLeft className="h-4 w-4" /> Powrót do forum</Link>
        </Button>

        <div className="rounded-2xl border border-border bg-card p-7" data-testid="topic-main">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {topic.pinned && <Badge className="gap-1 bg-ztk-yellow text-ztk-navydark hover:bg-ztk-yellow"><Pin className="h-3 w-3" /> Przypięty</Badge>}
            {topic.author_role === "admin" && <Badge className="gap-1 bg-ztk-blue hover:bg-ztk-blue"><Megaphone className="h-3 w-3" /> Ogłoszenie administratora</Badge>}
            {topic.solved && <Badge className="gap-1 bg-emerald-500 hover:bg-emerald-500"><CheckCircle2 className="h-3 w-3" /> Rozwiązany</Badge>}
          </div>
          <h1 className="font-display text-2xl font-bold text-ztk-navy dark:text-white lg:text-3xl">{topic.title}</h1>
          <div className="mt-3 flex items-center gap-3 text-sm text-muted-foreground">
            <Avatar className="h-8 w-8"><AvatarFallback className="bg-ztk-blue/10 text-xs font-semibold text-ztk-blue">{initials(topic.author_name)}</AvatarFallback></Avatar>
            <span className="font-medium text-ztk-navy dark:text-white">{topic.author_name}</span>
            <span>·</span>
            <span>{new Date(topic.created_at).toLocaleString("pl-PL")}</span>
          </div>
          <p className="mt-5 whitespace-pre-wrap leading-relaxed text-slate-700 dark:text-slate-300">{topic.body}</p>
          <Attachments items={topic.attachments} />
          <div className="mt-6 flex items-center gap-3 border-t border-border pt-4">
            <Button variant="outline" size="sm" onClick={likeTopic} data-testid="topic-like-button" className="gap-1.5">
              <Heart className={`h-4 w-4 ${topic.liked ? "fill-ztk-red text-ztk-red" : ""}`} /> {topic.like_count}
            </Button>
            {canManage(topic.author_id) && (
              <>
                <Button variant="outline" size="sm" onClick={toggleSolved} data-testid="topic-solve-button" className="gap-1.5">
                  <CheckCircle2 className="h-4 w-4" /> {topic.solved ? "Cofnij" : "Rozwiązany"}
                </Button>
                <Button variant="ghost" size="sm" onClick={deleteTopic} data-testid="topic-delete-button" className="gap-1.5 text-destructive hover:text-destructive">
                  <Trash2 className="h-4 w-4" /> Usuń
                </Button>
              </>
            )}
          </div>
        </div>

        <h2 className="mb-4 mt-10 font-display text-lg font-semibold text-ztk-navy dark:text-white">
          Odpowiedzi ({topic.replies.length})
        </h2>
        <div className="space-y-4">
          {topic.replies.map((r) => (
            <div key={r.id} data-testid={`reply-${r.id}`} className="rounded-xl border border-border bg-card p-5">
              <div className="flex items-center gap-2.5 text-sm">
                <Avatar className="h-7 w-7"><AvatarFallback className="bg-ztk-navy/10 text-xs font-semibold text-ztk-navy dark:text-white">{initials(r.author_name)}</AvatarFallback></Avatar>
                <span className="font-medium text-ztk-navy dark:text-white">{r.author_name}</span>
                <span className="text-xs text-muted-foreground">· {new Date(r.created_at).toLocaleString("pl-PL")}</span>
              </div>
              <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-700 dark:text-slate-300">{r.body}</p>
              <Attachments items={r.attachments} />
              <div className="mt-3 flex items-center gap-2">
                <button onClick={() => likeReply(r.id)} data-testid={`reply-like-${r.id}`} className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-ztk-red">
                  <Heart className={`h-4 w-4 ${r.liked ? "fill-ztk-red text-ztk-red" : ""}`} /> {r.like_count}
                </button>
                {canManage(r.author_id) && (
                  <button onClick={() => deleteReply(r.id)} data-testid={`reply-delete-${r.id}`} className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-destructive">
                    <Trash2 className="h-4 w-4" /> Usuń
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={submitReply} className="mt-8 rounded-2xl border border-border bg-card p-5" data-testid="reply-form">
          <Label className="font-display font-semibold text-ztk-navy dark:text-white">Twoja odpowiedź</Label>
          <Textarea required rows={4} value={reply} onChange={(e) => setReply(e.target.value)} data-testid="reply-input" className="mt-3" placeholder="Napisz odpowiedź..." />
          <div className="mt-3"><ForumAttach value={replyAtt} onChange={setReplyAtt} /></div>
          <Button type="submit" disabled={saving} className="mt-3 gap-1.5 bg-ztk-blue hover:bg-ztk-navy800" data-testid="reply-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Wyślij odpowiedź
          </Button>
        </form>
      </div>
    </div>
  );
}

function Label({ children, className }) {
  return <p className={className}>{children}</p>;
}
